import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Dumbbell, Utensils, Sparkles, Flame, Weight, 
  Droplet, Plus, Minus, ChevronRight, Zap, Target, Scale, 
  Trophy, Moon, ArrowUpRight, Star, 
  Activity, Award, Brain
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import Paywall from "@/components/Paywall";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, authHeaders, subscription, refreshSubscription } = useAuth();
  const [stats, setStats] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waterData, setWaterData] = useState({ glasses: 0, target_glasses: 8 });
  const [waterUpdating, setWaterUpdating] = useState(false);
  const [sleepStats, setSleepStats] = useState(null);
  const [searchParams] = (typeof window !== 'undefined' ? [new URLSearchParams(window.location.search)] : [new URLSearchParams()]);

  // Motivational quotes in Danish
  const quotes = [
    "Hver træning bringer dig tættere på dit mål",
    "Konsistens slår intensitet",
    "Du er stærkere end du tror",
    "Fremskridt, ikke perfektion",
    "Din eneste grænse er dig selv",
  ];
  const [dailyQuote] = useState(quotes[new Date().getDay() % quotes.length]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const isTrialStart = searchParams.get("trial") === "started";
    
    if (sessionId) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const res = await axios.get(`${API}/checkout/status/${sessionId}`, { headers: authHeaders() });
          if (res.data.payment_status === "paid") {
            refreshSubscription();
            window.history.replaceState({}, "", "/dashboard");
            if (isTrialStart) {
              toast.success("Velkommen! Din 3-dages gratis prøveperiode er nu aktiv");
            } else {
              toast.success("Betaling gennemført! Dit abonnement er nu aktivt.");
            }
            return;
          }
          setTimeout(() => pollStatus(attempts + 1), 2000);
        } catch {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      };
      pollStatus();
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, gamRes, waterRes, sleepRes] = await Promise.all([
          axios.get(`${API}/stats`, { headers: authHeaders() }),
          axios.get(`${API}/gamification/stats`, { headers: authHeaders() }),
          axios.get(`${API}/water-logs/today`, { headers: authHeaders() }),
          axios.get(`${API}/sleep-logs/stats`, { headers: authHeaders() }).catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setGamification(gamRes.data);
        setWaterData(waterRes.data);
        setSleepStats(sleepRes.data);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const updateWater = async (delta) => {
    const newGlasses = Math.max(0, waterData.glasses + delta);
    setWaterUpdating(true);
    try {
      const res = await axios.put(`${API}/water-logs/today`, 
        { glasses: newGlasses, target_glasses: waterData.target_glasses }, 
        { headers: authHeaders() }
      );
      setWaterData(res.data);
      if (newGlasses >= waterData.target_glasses && waterData.glasses < waterData.target_glasses) {
        toast.success("Dagligt vandmål nået!");
      }
    } catch {}
    setWaterUpdating(false);
  };

  const waterPercent = Math.min(100, (waterData.glasses / waterData.target_glasses) * 100);

  const chartData = stats?.recent_workouts?.slice(0, 7).reverse().map(w => ({
    name: new Date(w.date).toLocaleDateString("da-DK", { weekday: "short" }),
    kg: Math.round(w.total_weight || 0),
  })) || [];

  // Calculate daily goals progress
  const dailyGoals = {
    workout: stats?.recent_workouts?.some(w => w.date?.startsWith(new Date().toISOString().slice(0, 10))) || false,
    water: waterData.glasses >= waterData.target_glasses,
  };
  const completedGoals = Object.values(dailyGoals).filter(Boolean).length;
  const totalGoals = 2;

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "God morgen";
    if (hour < 18) return "God eftermiddag";
    return "God aften";
  };

  if (loading) {
    return (
      <Paywall>
        <div className="min-h-screen bg-[#09090b]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="bg-[#18181b] border border-white/5 rounded-xl h-40 animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </Paywall>
    );
  }

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="dashboard-page">
          
          {/* Header Row */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 gap-4">
            <div>
              <p className="text-zinc-500 text-sm mb-1">{getGreeting()}</p>
              <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-white" data-testid="dashboard-welcome">
                <span className="text-[#DFFF00]">{user?.name}</span>
              </h1>
              {subscription?.trial_active && (
                <div className="mt-2 inline-flex items-center gap-2 bg-[#DFFF00]/10 border border-[#DFFF00]/20 rounded-full px-3 py-1" data-testid="trial-banner">
                  <Zap className="h-3 w-3 text-[#DFFF00]" />
                  <span className="text-xs text-[#DFFF00]">{subscription.trial_days_left} dage tilbage af prøveperiode</span>
                </div>
              )}
            </div>
            
            {/* Daily Goals Ring */}
            <div className="flex items-center gap-4 bg-[#18181b] border border-white/5 rounded-xl px-5 py-3">
              <div className="relative w-14 h-14">
                <svg className="w-14 h-14 -rotate-90">
                  <circle cx="28" cy="28" r="24" stroke="#27272a" strokeWidth="4" fill="none" />
                  <circle 
                    cx="28" cy="28" r="24" 
                    stroke="#DFFF00" 
                    strokeWidth="4" 
                    fill="none"
                    strokeDasharray={`${(completedGoals / totalGoals) * 151} 151`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span style={{ fontFamily: 'JetBrains Mono' }} className="text-sm font-bold text-white">{completedGoals}/{totalGoals}</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Daglige mål</p>
                <p className="text-sm text-white font-medium">
                  {completedGoals === totalGoals ? "Alle fuldført!" : `${totalGoals - completedGoals} tilbage`}
                </p>
              </div>
            </div>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 auto-rows-[140px]">
            
            {/* XP & Level - Large Card */}
            <Link to="/achievements" className="col-span-2 row-span-2 group" data-testid="xp-card">
              <div className="h-full bg-gradient-to-br from-[#18181b] to-[#0f0f12] border border-white/5 rounded-xl p-5 hover:border-[#DFFF00]/30 transition-all relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#DFFF00]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="relative h-full flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/10 flex items-center justify-center">
                        <Trophy className="h-5 w-5 text-[#DFFF00]" />
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Level</p>
                        <p className="text-lg font-bold text-white">{gamification?.level?.level_name || "Begynder"}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-zinc-600 group-hover:text-[#DFFF00] transition-colors" />
                  </div>
                  
                  <div className="flex-1 flex flex-col justify-center">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span style={{ fontFamily: 'JetBrains Mono' }} className="text-4xl font-bold text-[#DFFF00]">
                        {gamification?.total_xp || 0}
                      </span>
                      <span className="text-zinc-500 text-sm">XP</span>
                    </div>
                    <Progress value={gamification?.level?.progress_percent || 0} className="h-2 bg-zinc-800" />
                    <p className="text-xs text-zinc-500 mt-2">
                      {gamification?.level?.next_level_xp 
                        ? `${gamification.level.next_level_xp - gamification.total_xp} XP til ${gamification.level.next_level_name}`
                        : "Max level nået!"}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/5">
                    <div className="flex items-center gap-1.5">
                      <Flame className="h-4 w-4 text-orange-400" />
                      <span className="text-sm text-white">{gamification?.current_streak || 0}</span>
                      <span className="text-xs text-zinc-500">streak</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Award className="h-4 w-4 text-purple-400" />
                      <span className="text-sm text-white">{gamification?.achievements_count || 0}</span>
                      <span className="text-xs text-zinc-500">badges</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Træninger Stat */}
            <div className="col-span-1 bg-gradient-to-br from-[#DFFF00]/10 to-transparent border border-white/5 rounded-xl p-4 hover:border-[#DFFF00]/20 transition-all">
              <Dumbbell className="h-5 w-5 text-[#DFFF00] mb-3" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Træninger</p>
              <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl font-bold text-[#DFFF00]" data-testid="stat-træninger">
                {stats?.total_workouts || 0}
              </p>
            </div>

            {/* Total Vægt Stat */}
            <div className="col-span-1 bg-gradient-to-br from-blue-500/10 to-transparent border border-white/5 rounded-xl p-4 hover:border-blue-500/20 transition-all">
              <Weight className="h-5 w-5 text-blue-400 mb-3" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Vægt</p>
              <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl font-bold text-blue-400">
                {((stats?.total_weight || 0) / 1000).toFixed(1)}t
              </p>
            </div>

            {/* AI Coach Quick Action */}
            <Link to="/ai-coach" className="col-span-2 row-span-1 group" data-testid="ai-coach-card">
              <div className="h-full bg-gradient-to-r from-purple-500/10 via-[#18181b] to-[#18181b] border border-white/5 rounded-xl p-4 hover:border-purple-500/30 transition-all flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-6 w-6 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white">AI Coach</p>
                  <p className="text-xs text-zinc-500 truncate">Generér personlig trænings- eller madplan</p>
                </div>
                <ArrowUpRight className="h-5 w-5 text-zinc-600 group-hover:text-purple-400 transition-colors flex-shrink-0" />
              </div>
            </Link>

            {/* Water Tracker */}
            <div className="col-span-2 row-span-2 bg-gradient-to-br from-blue-500/10 via-[#18181b] to-[#18181b] border border-white/5 rounded-xl p-5" data-testid="water-tracker-widget">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Droplet className="h-5 w-5 text-blue-400" />
                  <span style={{ fontFamily: 'Oswald' }} className="uppercase text-sm tracking-wider text-white">Vandindtag</span>
                </div>
                <span className="text-xs text-zinc-500">I dag</span>
              </div>
              
              {/* Liquid Fill Visual */}
              <div className="relative h-28 bg-zinc-900/50 rounded-xl overflow-hidden mb-4">
                <div 
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-500 to-blue-400/50 transition-all duration-500"
                  style={{ height: `${waterPercent}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p style={{ fontFamily: 'JetBrains Mono' }} className="text-3xl font-bold text-white drop-shadow-lg" data-testid="water-glasses">
                      {waterData.glasses}
                    </p>
                    <p className="text-xs text-white/70">af {waterData.target_glasses} glas</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-center gap-4">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => updateWater(-1)} 
                  disabled={waterUpdating || waterData.glasses === 0}
                  className="border-zinc-700 text-zinc-400 hover:text-white hover:border-blue-400 rounded-lg h-10 w-10"
                  data-testid="water-decrease-btn"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Progress value={waterPercent} className="flex-1 h-2 bg-zinc-800" />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => updateWater(1)} 
                  disabled={waterUpdating}
                  className="border-zinc-700 text-zinc-400 hover:text-white hover:border-blue-400 rounded-lg h-10 w-10"
                  data-testid="water-increase-btn"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Activity Chart - Wide */}
            <div className="col-span-2 md:col-span-4 row-span-2 bg-[#18181b] border border-white/5 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <div>
                  <h2 style={{ fontFamily: 'Oswald' }} className="uppercase text-sm tracking-wider text-white">Trænings Aktivitet</h2>
                  <p className="text-xs text-zinc-500">Vægt løftet (kg) - seneste 7 dage</p>
                </div>
                <Link to="/training" className="text-xs text-[#DFFF00] hover:underline flex items-center gap-1">
                  Se alle <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="p-4 h-[calc(100%-60px)]" data-testid="dashboard-chart">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorKg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#DFFF00" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#DFFF00" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                      <Tooltip 
                        contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} 
                        formatter={(value) => [`${value} kg`, 'Vægt']}
                      />
                      <Area type="monotone" dataKey="kg" stroke="#DFFF00" strokeWidth={2} fillOpacity={1} fill="url(#colorKg)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-zinc-500">
                    <Dumbbell className="h-10 w-10 mb-3 opacity-20" />
                    <p className="text-sm">Ingen træningsdata endnu</p>
                    <Link to="/training" className="mt-2 text-xs text-[#DFFF00] hover:underline">
                      Log din første træning
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Sleep Stats */}
            <Link to="/sleep-tracker" className="col-span-1 group" data-testid="sleep-card">
              <div className="h-full bg-gradient-to-br from-indigo-500/10 to-transparent border border-white/5 rounded-xl p-4 hover:border-indigo-500/20 transition-all">
                <Moon className="h-5 w-5 text-indigo-400 mb-3" />
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Søvn</p>
                <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-indigo-400">
                  {sleepStats?.avg_hours?.toFixed(1) || "0"}t
                </p>
                <p className="text-xs text-zinc-500">gns./nat</p>
              </div>
            </Link>

            {/* Madplaner */}
            <Link to="/meals" className="col-span-1 group" data-testid="meals-card">
              <div className="h-full bg-gradient-to-br from-emerald-500/10 to-transparent border border-white/5 rounded-xl p-4 hover:border-emerald-500/20 transition-all">
                <Utensils className="h-5 w-5 text-emerald-400 mb-3" />
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Madplaner</p>
                <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl font-bold text-emerald-400">
                  {stats?.meals_count || 0}
                </p>
              </div>
            </Link>

            {/* Leaderboard Position */}
            <Link to="/leaderboard" className="col-span-1 group" data-testid="leaderboard-card">
              <div className="h-full bg-gradient-to-br from-amber-500/10 to-transparent border border-white/5 rounded-xl p-4 hover:border-amber-500/20 transition-all">
                <Star className="h-5 w-5 text-amber-400 mb-3" />
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Ranking</p>
                <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-amber-400">
                  Top {100 - (gamification?.level?.progress_percent || 0)}%
                </p>
              </div>
            </Link>

            {/* Quick Actions Row */}
            <div className="col-span-2 md:col-span-4 lg:col-span-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { to: "/training", label: "Log Træning", icon: Dumbbell, color: "text-[#DFFF00]", bg: "from-[#DFFF00]/10" },
                { to: "/body-tracker", label: "Krops Tracker", icon: Scale, color: "text-pink-400", bg: "from-pink-500/10" },
                { to: "/exercises", label: "Øvelser", icon: Activity, color: "text-cyan-400", bg: "from-cyan-500/10" },
                { to: "/goals", label: "Mål & Fremskridt", icon: Target, color: "text-orange-400", bg: "from-orange-500/10" },
              ].map(({ to, label, icon: Icon, color, bg }) => (
                <Link to={to} key={to} data-testid={`quick-action-${to.slice(1)}`}>
                  <div className={`bg-gradient-to-r ${bg} to-transparent bg-[#18181b] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-all group flex items-center gap-3`}>
                    <div className={`w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center ${color} group-hover:scale-110 transition-transform`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-sm font-medium text-white">{label}</span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Motivation Quote */}
            <div className="col-span-2 md:col-span-4 lg:col-span-6 bg-gradient-to-r from-[#18181b] via-[#1a1a1f] to-[#18181b] border border-white/5 rounded-xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#DFFF00]/10 flex items-center justify-center flex-shrink-0">
                  <Brain className="h-6 w-6 text-[#DFFF00]" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm italic">"{dailyQuote}"</p>
                  <p className="text-xs text-zinc-600 mt-1">Dagens motivation</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Paywall>
  );
}
